import { useState } from "react";
import useTranslator from "../../hooks/useTranslator";
import ProfileCompletionPercentage from "./ProfileCompletionPercentage";
import { Tooltip as ReactTooltip } from "react-tooltip";
import styles from "./TenantHeaderCard.module.css";

const TenantHeaderCard = ({
  percent,
  percentageLoading = false,
  missingDocs = [],
  kycStatus = "Failed",
  kycState = "failed",
  bankConnected = false,
  rentalStatus = "Elevated",
  guarantorCount = 0,
  flatmateCount = 0,
  onRedoKyc,
  onConnectBank,
  onAddGuarantor,
  onAddFlatmate,
  onEditLandlordNote,
  disabled = false,
}) => {
  const { _t } = useTranslator();
  const [expanded, setExpanded] = useState(false);
  const visibleDocs = expanded ? missingDocs : missingDocs.slice(0, 3);
  const normalizedRentalStatus = String(rentalStatus || "Elevated");
  const isKycFailed = kycState === "failed";
  const isRentalElevated = normalizedRentalStatus.toLowerCase() === "elevated";
  const disabledStyle = disabled
    ? { opacity: 0.5, cursor: "not-allowed" }
    : undefined;
  const disabledTooltip = disabled
    ? _t(
        "profileOutdatedRenewFirst",
        "Your profile is outdated. Please renew your profile first."
      )
    : undefined;

  const renderTooltip = (id) =>
    disabled ? (
      <ReactTooltip
        id={id}
        place="top"
        className="react-tooltip-styling"
        render={() => <p>{disabledTooltip}</p>}
      />
    ) : null;

  return (
    <section className={styles.card} aria-label="tenant overview">
      <div className={styles.top}>
        <div className={styles.left}>
          <ProfileCompletionPercentage
            percent={percent}
            loading={percentageLoading}
          />
          <div className={styles.missing}>
            <div className={styles.missingText}>
              {_t(
                "docsAwayFromComplete",
                `${missingDocs.length} documents away from a complete profile`,
                undefined,
                { count: missingDocs.length }
              )}
            </div>
            <div className={styles.chips}>
              {visibleDocs.map((d, i) => (
                <span key={i} className={styles.chip}>
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                  </svg>
                  {_t(
                    typeof d === "string" ? d : d[0],
                    typeof d === "string" ? d : d[0],
                    "fixed"
                  )}
                </span>
              ))}
              {missingDocs.length > 3 && (
                <button
                  type="button"
                  className={styles.moreLink}
                  onClick={() => setExpanded((v) => !v)}
                >
                  {expanded
                    ? _t("less", "Less")
                    : `+${missingDocs.length - 3} ${_t("more", "more")}`}
                </button>
              )}
            </div>
          </div>
        </div>
        <aside className={styles.note} aria-labelledby="landlord-note-title">
          <div className={styles.noteHeader}>
            <span id="landlord-note-title" className={styles.noteTitle}>
              {_t("aNoteToLandlords", "A note to landlords")}
            </span>
            {disabled ? (
              <div
                style={{ display: "inline-block" }}
                data-tooltip-id="thc-edit-tooltip"
              >
                <button
                  type="button"
                  className={styles.action}
                  onClick={onEditLandlordNote}
                  disabled={disabled}
                  style={disabledStyle}
                >
                  {_t("edit", "Edit")}
                </button>
                {renderTooltip("thc-edit-tooltip")}
              </div>
            ) : (
              <button
                type="button"
                className={styles.action}
                onClick={onEditLandlordNote}
              >
                {_t("edit", "Edit")}
              </button>
            )}
          </div>
        </aside>
      </div>

      <div className={styles.grid}>
        <div className={styles.box}>
          <div className={styles.boxLabel}>
            {_t("verification", "Verification")}
          </div>
          <div
            className={`${styles.boxValue} ${isKycFailed ? styles.danger : ""}`}
          >
            KYC · {kycStatus}{" "}
            {disabled ? (
              <div
                style={{ display: "inline-block", marginLeft: "auto" }}
                data-tooltip-id="thc-redo-tooltip"
              >
                <button
                  type="button"
                  className={styles.action}
                  onClick={onRedoKyc}
                  disabled={disabled}
                  style={disabledStyle}
                >
                  {_t("redo", "Redo")}
                </button>
                {renderTooltip("thc-redo-tooltip")}
              </div>
            ) : (
              <button
                type="button"
                className={styles.action}
                onClick={onRedoKyc}
              >
                {_t("redo", "Redo")}
              </button>
            )}
          </div>
        </div>
        <div className={styles.box}>
          <div className={styles.boxLabel}>
            {_t("bankBehaviour", "Bank behaviour")}
          </div>
          <div
            className={`${styles.boxValue} ${!bankConnected ? styles.danger : ""}`}
          >
            {bankConnected
              ? _t("banklinked", "Linked")
              : _t("banknotLinked", "Not linked")}{" "}
            {!bankConnected &&
              (disabled ? (
                <div
                  style={{ display: "inline-block", marginLeft: "auto" }}
                  data-tooltip-id="thc-connect-tooltip"
                >
                  <button
                    type="button"
                    className={styles.action}
                    onClick={onConnectBank}
                    disabled={disabled}
                    style={disabledStyle}
                  >
                    {_t("connect", "Connect")}
                  </button>
                  {renderTooltip("thc-connect-tooltip")}
                </div>
              ) : (
                <button
                  type="button"
                  className={styles.action}
                  onClick={onConnectBank}
                >
                  {_t("connect", "Connect")}
                </button>
              ))}
          </div>
        </div>
        <div className={styles.box}>
          <div className={styles.boxLabel}>
            {_t("rentalBarometer", "Rental barometer")}
          </div>
          <div
            className={`${styles.boxValue} ${isRentalElevated ? styles.danger : ""}`}
          >
            {_t(normalizedRentalStatus.toLowerCase(), normalizedRentalStatus)}
          </div>
        </div>
        <div className={styles.box}>
          <div className={styles.boxLabel}>{_t("guarantor", "Guarantor")}</div>
          <div className={styles.boxValue}>
            {guarantorCount
              ? `${guarantorCount} ${_t("added", "added")}`
              : _t("none", "None")}{" "}
            {disabled ? (
              <div
                style={{ display: "inline-block", marginLeft: "auto" }}
                data-tooltip-id="thc-guarantor-tooltip"
              >
                <button
                  type="button"
                  className={styles.action}
                  onClick={onAddGuarantor}
                  disabled={disabled}
                  style={disabledStyle}
                >
                  + {_t("add", "Add")}
                </button>
                {renderTooltip("thc-guarantor-tooltip")}
              </div>
            ) : (
              <button
                type="button"
                className={styles.action}
                onClick={onAddGuarantor}
              >
                + {_t("add", "Add")}
              </button>
            )}
          </div>
        </div>
        <div className={styles.box}>
          <div className={styles.boxLabel}>{_t("flatmates", "Flatmates")}</div>
          <div className={styles.boxValue}>
            {flatmateCount || 0}{" "}
            {disabled ? (
              <div
                style={{ display: "inline-block", marginLeft: "auto" }}
                data-tooltip-id="thc-flatmate-tooltip"
              >
                <button
                  type="button"
                  className={styles.action}
                  onClick={onAddFlatmate}
                  disabled={disabled}
                  style={disabledStyle}
                >
                  + {_t("add", "Add")}
                </button>
                {renderTooltip("thc-flatmate-tooltip")}
              </div>
            ) : (
              <button
                type="button"
                className={styles.action}
                onClick={onAddFlatmate}
              >
                + {_t("add", "Add")}
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TenantHeaderCard;
